import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Church, ArrowRight } from "@phosphor-icons/react";

// Sunday Service Reminder Banner.
// ----------------------------------------------------------------------------
// Appears Saturday evenings (6pm+ local time) and stays up through Sunday
// noon — the prime window when faith-app users plan / attend services.
// Surfaces a "Sunday Service Tomorrow" / "Live Now" banner on Home with
// a deep link to /live. Hides itself outside that window so it never
// becomes wallpaper.
//
// Calendar-only logic, no backend call — keeps Home renders fast.
// Worship times are inherently a "current moment" piece of UI; backend
// scheduling would only matter if we needed per-user reminder push, and
// that's already covered by our web-push notification pipeline.
function isSundayReminderWindow(now = new Date()) {
  const day = now.getDay(); // 0 = Sunday, 6 = Saturday
  const hour = now.getHours();
  // Saturday evening — 6 PM through midnight.
  if (day === 6 && hour >= 18) return { kind: "tomorrow", label: "Sunday Service Tomorrow" };
  // Sunday morning — midnight through service window (noon).
  if (day === 0 && hour < 12) {
    if (hour >= 9 && hour < 12) return { kind: "live", label: "Sunday Service · Live Now" };
    return { kind: "today", label: "Sunday Service Today" };
  }
  return null;
}

export default function SundayServiceBanner() {
  const [window, setWindow] = useState(() => isSundayReminderWindow());

  useEffect(() => {
    // Re-check every 5 minutes so a banner that should appear/disappear
    // at a specific hour does so without a manual page reload.
    const id = setInterval(() => setWindow(isSundayReminderWindow()), 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  if (!window) return null;

  const isLive = window.kind === "live";

  return (
    <Link
      to="/live"
      data-testid="sunday-service-banner"
      className={`block mt-6 mx-4 sm:mx-6 lg:mx-10 rounded-2xl border-2 px-5 py-4 transition-all hover:shadow-md ${
        isLive
          ? "border-terracotta bg-terracotta text-cream"
          : "border-forest bg-cream text-forest"
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`w-12 h-12 rounded-xl grid place-items-center shrink-0 ${
            isLive ? "bg-cream/20" : "bg-forest/10"
          }`}
        >
          <Church size={24} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {isLive && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cream text-terracotta text-[9px] font-bold uppercase tracking-[0.18em]"
                data-testid="live-badge"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-terracotta animate-pulse" /> Live
              </span>
            )}
            <p className="font-heading text-base sm:text-lg font-medium">
              {window.label}
            </p>
          </div>
          <p
            className={`text-xs sm:text-sm mt-1 ${
              isLive ? "text-cream/80" : "text-muted_ink"
            }`}
          >
            {isLive
              ? "Tap to join your congregation right now."
              : "Tap to see today's lineup of live sermons and worship."}
          </p>
        </div>
        <ArrowRight size={18} weight="bold" className="shrink-0" />
      </div>
    </Link>
  );
}
